import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { FiLock } from 'react-icons/fi';

export default function UpdatePasswordPage() {
  const { session } = useAuth();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for password recovery mode in URL hash
    const isPasswordRecovery = window.location.hash.includes('type=recovery');
    
    if (!session?.access_token && !isPasswordRecovery) {
      navigate('/login');
    }
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) throw error;
      
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err: any) {
      console.error('Password update error:', err);
      setError(err.message || t('terminal.auth.errors.passwordUpdateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-4">
      <div className="max-w-md w-full space-y-8 p-8 bg-base-100 rounded-lg shadow-xl border border-primary/10">
        <div className="text-center">
          <h2 className="glitch text-2xl font-bold" data-text={t('terminal.auth.updatePassword.title')}>
            {t('terminal.auth.updatePassword.title')}
          </h2>
          <p className="mt-2 text-sm text-base-content/70">
            {t('terminal.auth.updatePassword.description')}
          </p>
        </div>

        {success ? (
          <div className="alert alert-success">
            <FiLock className="w-6 h-6" />
            <span>{t('terminal.auth.updatePassword.success')}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {error && (
              <div className="alert alert-error">
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="password" className="label">
                <span className="label-text">{t('terminal.auth.newPassword')}</span>
              </label>
              <input
                id="password"
                type="password"
                className="input input-bordered w-full"
                placeholder={t('terminal.auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <button 
              type="submit" 
              className={`btn btn-primary w-full ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? t('common.updating') : t('terminal.auth.updatePassword.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
